import * as React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useSubscriptions } from '@/features/accounts/api/get-subscriptions'

type Props = {
    accountId: string
}

function formatDate(value: string | null | undefined) {
    if (!value) return '—'
    try {
        const d = new Date(value)
        return isNaN(d.getTime()) ? value : d.toLocaleDateString()
    } catch {
        return value
    }
}

export default function AccountDetailSubscription({ accountId }: Props) {
    const { data, isLoading, isError } = useSubscriptions({
        params: {
            accountId: accountId || undefined,
        },
    })

    if (isLoading) return <Card><CardHeader><CardTitle>Subscription</CardTitle></CardHeader><CardContent>Loading…</CardContent></Card>
    if (isError) return <Card><CardHeader><CardTitle>Subscription</CardTitle></CardHeader><CardContent>Failed to load subscription.</CardContent></Card>

    const subs = data?.items || []

    return (
        <Card>
            <CardHeader>
                <div className='flex items-center justify-between'>
                    <CardTitle>Subscriptions</CardTitle>
                    <Button asChild size='sm' variant='outline'>
                        <Link to={`/subscriptions/new?accountId=${accountId}`}>
                            <Plus className='h-4 w-4 mr-2' />
                            Add Subscription
                        </Link>
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {subs.length === 0 ? (
                    <div className='text-sm text-muted-foreground'>No subscriptions found for this account.</div>
                ) : (
                    <div className='space-y-4'>
                        {subs.map((subscription) => (
                            <Card key={subscription.subscriptionId} className='border'>
                                <CardContent className='pt-6'>
                                    <div className='space-y-3'>
                                        <div className='flex flex-wrap items-center gap-2'>
                                            <Badge variant={subscription.status?.toLowerCase() === 'active' ? 'default' : 'destructive'}>
                                                {subscription.status}
                                            </Badge>
                                            {subscription.scope && <Badge variant='outline'>Scope: {subscription.scope}</Badge>}
                                            {subscription.primaryFlag && <Badge variant='outline'>Primary</Badge>}
                                        </div>

                                        <div className='grid sm:grid-cols-2 gap-3'>
                                            <div>
                                                <div className='text-xs uppercase tracking-wide text-muted-foreground'>Subscription ID</div>
                                                <div className='text-sm break-all'>{subscription.subscriptionId}</div>
                                            </div>
                                            <div>
                                                <div className='text-xs uppercase tracking-wide text-muted-foreground'>Chargebee ID</div>
                                                {subscription.chargebeeSubscriptionId ? (
                                                    <a 
                                                        href={`https://4473cloud.chargebee.com/d/subscriptions/${subscription.chargebeeSubscriptionId}`}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        className='text-sm break-all text-primary hover:underline'
                                                    >
                                                        {subscription.chargebeeSubscriptionId}
                                                    </a>
                                                ) : (
                                                    <div className='text-sm break-all'>—</div>
                                                )}
                                            </div>
                                            <div>
                                                <div className='text-xs uppercase tracking-wide text-muted-foreground'>Current Period</div>
                                                <div className='text-sm'>{formatDate(subscription.currentPeriodStart)} → {formatDate(subscription.currentPeriodEnd)}</div>
                                            </div>
                                            <div>
                                                <div className='text-xs uppercase tracking-wide text-muted-foreground'>Renewal At</div>
                                                <div className='text-sm'>{formatDate(subscription.renewalAt)}</div>
                                            </div>
                                        </div>

                                        <div>
                                            <div className='text-xs uppercase tracking-wide text-muted-foreground mb-1'>Items</div>
                                            <div className='space-y-1'>
                                                {subscription.items && subscription.items.length > 0 ? (
                                                    subscription.items.map((item) => (
                                                        <div key={item.subscriptionItemId} className='flex items-center justify-between text-sm'>
                                                            <div className='flex items-center gap-2'>
                                                                {item.isBasePlan && <Badge variant='outline'>Base</Badge>}
                                                                <span>{item.plan?.name || item.planId}</span>
                                                            </div>
                                                            <div className='text-muted-foreground'>x{item.quantity}</div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className='text-sm text-muted-foreground'>No items</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    )
}


